import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { useCurricula, useEnrollCurriculum } from "@/hooks/useCurricula";
import { Skeleton } from "@/components/ui/skeleton";
import { BookOpen, GraduationCap, CheckCircle2, ClipboardList, Calendar, Lock } from "lucide-react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { getStorageUrl } from "@/lib/storage";
import { MyAssignmentsTab } from "@/components/learning/MyAssignmentsTab";
import { useUserAssignments, UserAssignment } from "@/hooks/useUserAssignments";
import { format, parseISO, differenceInDays, isPast, isToday } from "date-fns";
import { CurriculumProgressSummaryCard } from "@/components/lms/CurriculumProgressSummaryCard";
import { useLearnerCurriculumProgress, type CurriculumProgressSummary } from "@/hooks/useLearnerCurriculumProgress";

export default function Curricula() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const { data: curricula, isLoading } = useCurricula();
  const enrollMutation = useEnrollCurriculum();
  const { assignments, count: assignmentCount } = useUserAssignments();
  const { data: learnerProgress } = useLearnerCurriculumProgress();

  // Handle tab from URL parameter
  const tabFromUrl = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState<string>(
    tabFromUrl === "assignments" ? "assignments" : 
    tabFromUrl === "enrolled" ? "enrolled" : "all"
  );

  // Update URL when tab changes
  const handleTabChange = (value: string) => {
    setActiveTab(value);
    if (value === "all") {
      searchParams.delete("tab");
    } else {
      searchParams.set("tab", value);
    }
    setSearchParams(searchParams, { replace: true });
  };

  // Sync tab with URL on mount and URL changes
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "assignments") {
      setActiveTab("assignments");
    } else if (tab === "enrolled") {
      setActiveTab("enrolled");
    } else {
      setActiveTab("all");
    }
  }, [searchParams]);

  const handleEnroll = async (curriculumId: string) => {
    if (!user) {
      toast.error("Please sign in to enroll");
      navigate("/auth");
      return;
    }
    try {
      await enrollMutation.mutateAsync(curriculumId);
      toast.success("Successfully enrolled in curriculum!");
    } catch (error: any) {
      if (error?.message?.includes("row-level security") || error?.code === "42501") {
        toast.error("This curriculum is available by invitation only. Contact your instructor to be enrolled.");
      } else {
        toast.error("Failed to enroll");
      }
    }
  };

  // Create a map of curriculum assignments for quick lookup
  const curriculumAssignmentsMap = new Map<string, UserAssignment>();
  assignments
    .filter(a => a.type === 'curriculum')
    .forEach(a => curriculumAssignmentsMap.set(a.item_id, a));

  // Filter enrolled curricula
  const enrolledCurricula = curricula?.filter(c => c.is_enrolled) || [];

  // One lookup for the whole page, passed down to each card.
  const progressMap = new Map<string, CurriculumProgressSummary>();
  (learnerProgress ?? []).forEach(p => progressMap.set(p.curriculumId, p));

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold">Learning</h1>
          <p className="text-muted-foreground mt-1">
            Explore curricula and track your learning progress
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange}>
          <TabsList className="w-full sm:w-auto flex">
            <TabsTrigger value="all" className="flex-1 sm:flex-none">
              All Curricula
            </TabsTrigger>
            <TabsTrigger value="enrolled" className="flex-1 sm:flex-none">
              My Enrollments
              {enrolledCurricula.length > 0 && (
                <Badge variant="secondary" className="ml-2 hidden sm:inline-flex">
                  {enrolledCurricula.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="assignments" className="flex-1 sm:flex-none">
              <span className="truncate">My Assignments</span>
              {assignmentCount > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {assignmentCount}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* All Curricula Tab */}
          <TabsContent value="all" className="mt-6">
            {isLoading ? (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="space-y-3">
                    <Skeleton className="aspect-video w-full" />
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                ))}
              </div>
            ) : curricula && curricula.length > 0 ? (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {curricula.map((curriculum) => {
                  const thumbnailUrl = getStorageUrl("course-content", curriculum.thumbnail_url);
                  const assignment = curriculumAssignmentsMap.get(curriculum.id);
                  return (
                    <CurriculumCard
                      key={curriculum.id}
                      curriculum={curriculum}
                      thumbnailUrl={thumbnailUrl}
                      assignment={assignment}
                      progress={progressMap.get(curriculum.id)}
                      onEnroll={handleEnroll}
                      onContinue={() => navigate(`/curricula/${curriculum.id}`)}
                      isEnrolling={enrollMutation.isPending}
                    />
                  );
                })}
              </div>
            ) : (
              <EmptyState
                title="No curricula available for your role"
                description="Check back soon! New curricula may be added that match your profile."
              />
            )}
          </TabsContent>

          {/* My Enrollments Tab */}
          <TabsContent value="enrolled" className="mt-6">
            {isLoading ? (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="space-y-3">
                    <Skeleton className="aspect-video w-full" />
                    <Skeleton className="h-6 w-3/4" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                ))}
              </div>
            ) : enrolledCurricula.length > 0 ? (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {enrolledCurricula.map((curriculum) => {
                  const thumbnailUrl = getStorageUrl("course-content", curriculum.thumbnail_url);
                  const assignment = curriculumAssignmentsMap.get(curriculum.id);
                  return (
                    <CurriculumCard
                      key={curriculum.id}
                      curriculum={curriculum}
                      thumbnailUrl={thumbnailUrl}
                      assignment={assignment}
                      progress={progressMap.get(curriculum.id)}
                      onEnroll={handleEnroll}
                      onContinue={() => navigate(`/curricula/${curriculum.id}`)}
                      isEnrolling={enrollMutation.isPending}
                    />
                  );
                })}
              </div>
            ) : (
              <EmptyState
                title="No enrollments yet"
                description="Enroll in a curriculum to start learning and track your progress here."
              />
            )}
          </TabsContent>

          {/* My Assignments Tab */}
          <TabsContent value="assignments" className="mt-6">
            <MyAssignmentsTab />
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}

// Extracted components for cleaner code

interface CurriculumCardProps {
  curriculum: {
    id: string;
    title: string;
    description?: string | null;
    thumbnail_url?: string | null;
    courses_count?: number;
    is_enrolled?: boolean;
    is_cohort_enrolled?: boolean;
    access_mode?: string;
  };
  thumbnailUrl: string | null;
  assignment?: UserAssignment;
  /** Passed in from the parent so the progress hook runs once, not once per card. */
  progress?: CurriculumProgressSummary;
  onEnroll: (id: string) => void;
  onContinue: () => void;
  isEnrolling: boolean;
}

function CurriculumCard({ curriculum, thumbnailUrl, assignment, progress, onEnroll, onContinue, isEnrolling }: CurriculumCardProps) {
  // Calculate due date status
  const getDueDateStatus = (dueDate: string | null | undefined) => {
    if (!dueDate) return null;
    
    const date = parseISO(dueDate);
    const daysUntilDue = differenceInDays(date, new Date());
    const isOverdue = isPast(date) && !isToday(date);
    const isDueToday = isToday(date);
    const isNearDue = daysUntilDue <= 7 && daysUntilDue >= 0;
    
    return {
      text: format(date, "MMM d, yyyy"),
      isOverdue,
      isDueToday,
      isNearDue,
      daysUntilDue,
    };
  };

  const dueDateStatus = assignment?.due_date ? getDueDateStatus(assignment.due_date) : null;

  return (
    <Card className="overflow-hidden flex flex-col">
      <div className="aspect-video bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center relative">
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={curriculum.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <GraduationCap className="h-16 w-16 text-primary/50" />
        )}
        
        {/* Assignment badge overlay on thumbnail */}
        {assignment && (
          <div className="absolute top-2 right-2 flex flex-col items-end gap-1">
            <Badge variant="secondary" className="bg-background/90 backdrop-blur-sm">
              <ClipboardList className="h-3 w-3 mr-1" />
              Assigned
            </Badge>
            {dueDateStatus && (
              <Badge 
                variant="outline" 
                className={`text-xs bg-background/90 backdrop-blur-sm ${
                  dueDateStatus.isOverdue 
                    ? 'border-destructive text-destructive' 
                    : dueDateStatus.isDueToday || dueDateStatus.isNearDue
                      ? 'border-warning text-warning'
                      : ''
                }`}
              >
                <Calendar className="h-3 w-3 mr-1" />
                {dueDateStatus.isOverdue 
                  ? `Overdue` 
                  : dueDateStatus.isDueToday 
                    ? 'Due today'
                    : `Due: ${dueDateStatus.text}`}
              </Badge>
            )}
          </div>
        )}
      </div>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-lg line-clamp-2">{curriculum.title}</CardTitle>
          {curriculum.is_enrolled && (
            <Badge variant="secondary" className="shrink-0">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Enrolled
            </Badge>
          )}
        </div>
        <CardDescription className="line-clamp-2">
          {curriculum.description || "No description available"}
        </CardDescription>
      </CardHeader>
      <CardContent className="pb-2 flex-1 space-y-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <BookOpen className="h-4 w-4" />
          <span>{curriculum.courses_count || 0} courses</span>
        </div>
        {/* Only for enrolled learners — an un-enrolled card has no progress to show. */}
        {curriculum.is_enrolled && progress && (
          <CurriculumProgressSummaryCard summary={progress} showTitle={false} />
        )}
      </CardContent>
      <CardFooter>
        {curriculum.is_enrolled ? (
          <Button className="w-full" onClick={onContinue}>
            Continue Learning
          </Button>
        ) : curriculum.access_mode === 'cohort_only' && !curriculum.is_cohort_enrolled ? (
          <div className="w-full space-y-2 text-center">
            <div className="flex items-center justify-center gap-2 text-muted-foreground">
              <Lock className="h-4 w-4" />
              <span className="text-sm font-medium">Available by invitation only</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Contact your instructor to be enrolled in this curriculum.
            </p>
          </div>
        ) : (
          <Button
            className="w-full"
            onClick={() => onEnroll(curriculum.id)}
            disabled={isEnrolling}
          >
            {isEnrolling ? "Enrolling..." : "Enroll Now"}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
}

function EmptyState({ title, description }: { title: string; description: string }) {
  return (
    <div className="text-center py-16 px-4">
      <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
        <GraduationCap className="h-8 w-8 text-muted-foreground" />
      </div>
      <h3 className="text-lg font-medium mb-2">{title}</h3>
      <p className="text-muted-foreground max-w-md mx-auto">{description}</p>
    </div>
  );
}
